import { useLinkableEntityFiles } from '@commenda/api/drive-view/useLinkableEntityFiles';
import { Alert, Badge, Icons, SingleCombobox } from '@commenda/kakashi';
import { IDriveViewLinkableFile } from '@commenda/types/src/drive-view/types';
import { cn, documentName, getFileComponents } from '@commenda/utils';
import { LegacyDocumentType } from '@prisma/client';
import { useState } from 'react';
import { useDebounce } from '../../../../../hooks/useDebounce';

const UNTYPED_DOCUMENT_TYPES: (LegacyDocumentType | null)[] = [
  null,
  LegacyDocumentType.Other,
  LegacyDocumentType.Custom,
];

const isTyped = (file: IDriveViewLinkableFile) =>
  !UNTYPED_DOCUMENT_TYPES.includes(file.documentType);

const typeName = (file: IDriveViewLinkableFile) =>
  file.documentName ?? documentName(file.documentType ?? undefined);

const LinkableFileRow = ({ file }: { file: IDriveViewLinkableFile }) => {
  const { fileTypeIcon: FileTypeIcon } = getFileComponents(file.fileName);

  return (
    <div className="flex min-w-0 flex-1 items-center gap-lg">
      <FileTypeIcon className="size-6 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col text-left">
        <span className="txt-small-medium truncate text-text-primary">
          {file.fileName}
        </span>
        {file.folderPath ? (
          <span className="txt-extra-small-regular truncate text-text-tertiary">
            {file.folderPath}
          </span>
        ) : null}
      </div>
      {isTyped(file) ? (
        <Badge variant="gray" size="sm">
          {typeName(file)}
        </Badge>
      ) : null}
    </div>
  );
};

export const ExistingDocumentSelect = ({
  corporationId,
  documentType,
  documentLabel,
  selectedFile,
  onSelect,
}: {
  corporationId: number;
  documentType: LegacyDocumentType;
  documentLabel: string;
  selectedFile: IDriveViewLinkableFile | null;
  onSelect: (file: IDriveViewLinkableFile | null) => void;
}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, error, isLoading } = useLinkableEntityFiles({
    corporationId,
    excludeDocumentType: documentType,
    search: debouncedSearch,
  });

  const files = data?.items ?? [];
  const filesById = new Map(files.map((file) => [String(file.fileId), file]));
  const options = files.map((file) => ({
    value: String(file.fileId),
    label: file.fileName,
  }));

  const notFoundText = isLoading
    ? 'Loading documents…'
    : debouncedSearch.trim()
      ? `No documents match "${debouncedSearch.trim()}"`
      : 'This entity has no documents yet. Upload a file above instead.';

  return (
    <div className="flex flex-col gap-lg">
      <SingleCombobox
        modalPopover
        options={options}
        value={selectedFile ? String(selectedFile.fileId) : ''}
        setValue={(value) => onSelect(filesById.get(value) ?? null)}
        placeholder="Select a document"
        searchPlaceholder="Search by file name"
        onSearchChange={setSearch}
        notFoundText={notFoundText}
        useRenderTriggerForPlaceholder
        renderTrigger={({ placeholder }) =>
          selectedFile ? (
            <span className="min-w-0 flex-1 truncate text-left">
              {selectedFile.fileName}
            </span>
          ) : (
            <span className="txt-small-regular flex-1 text-left text-text-placeholder">
              {placeholder}
            </span>
          )
        }
        renderOption={({ option, isSelected }) => {
          const file = filesById.get(option.value);
          if (!file) return null;

          return (
            <div className="flex w-full min-w-0 items-center gap-md">
              <LinkableFileRow file={file} />
              <Icons.Check
                className={cn(
                  'size-4 shrink-0 text-fg-brand',
                  isSelected ? 'opacity-100' : 'opacity-0'
                )}
              />
            </div>
          );
        }}
      />
      {error ? (
        <Alert
          variant="destructive"
          title="Couldn't load documents"
          description="Try again, or upload a file above instead."
        />
      ) : data?.hasMore || selectedFile ? (
        <p className="txt-extra-small-regular text-text-tertiary">
          {data?.hasMore
            ? 'Showing the most recently updated documents. Search to find others. '
            : ''}
          {selectedFile
            ? `This uses the same document, not a copy. It will be filed under ${documentLabel} in Documents, unless it's already in a folder you made.`
            : ''}
        </p>
      ) : null}
      {selectedFile && isTyped(selectedFile) ? (
        <Alert
          variant="warning"
          title={`Already filed as ${typeName(selectedFile)}`}
          description={`Adding it here changes its type to ${documentLabel}.`}
        />
      ) : null}
    </div>
  );
};
